import { useTranslation } from 'gatsby-plugin-react-i18next';
import React, { ChangeEvent, useCallback, useMemo, useState } from 'react';
import Picker, { SourceOption } from '../Picker';
import Text, { TextTypes } from '../Text';
import Input from '../Input';
import useContactFrom, { ResponseState } from '../../hooks/useContactForm';
import Loader from '../Loader';
import Button from '../Button';
import classNames from 'classnames';
import { TFunction } from 'i18next';

type ContactFormProps = {
  className?: string;
  onSuccess?: () => void;
  titleTagType?: TextTypes;
};

const getSourceOptions = (t: TFunction): SourceOption[] => [
  { value: '', body: t('contact_form_source_title'), disabled: true },
  { value: 'google', body: t('google') },
  { value: 'facebook', body: t('facebook') },
  { value: 'reference', body: t('contact_form_source_reference') },
  { value: 'guestarticle', body: t('contact_form_source_article') },
];

const ContactForm: React.FC<ContactFormProps> = ({ onSuccess }) => {
  const { t } = useTranslation();

  const [isFormHidden, setIsFormHidden] = useState(false);

  const [
    values,
    errors,
    { setName, setEmail, setDescription, setSource },
    onSubmit,
    responseState,
    isProcessing,
    isSuccess,
  ] = useContactFrom({ onSuccess });

  const sourceOptions = useMemo(() => getSourceOptions(t), [t]);

  const onSourceChange = useCallback<
    (e: ChangeEvent<HTMLSelectElement>) => void
  >((e) => setSource(e.target.value), [setSource]);

  const onFormHideAnimationEnd = useCallback(() => {
    setIsFormHidden(true);
  }, []);

  const handleFormSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      onSubmit();
    },
    [onSubmit]
  );

  const SubmitButton = useMemo(() => {
    return isProcessing && responseState !== ResponseState.Success ? (
      <Loader className="contact__form__loader" />
    ) : (
      <Button
        color="secondary"
        size="regular"
        className="contact__form__submit-button"
        rounded
      >
        {t('send')}
      </Button>
    );
  }, [isProcessing, responseState, t]);

  return (
    <div className="contact">
      <Text type="p" color="light" className="contact__form__title">
        {t('contact_form_message_us')}
      </Text>
      <form
        className={classNames('contact__form', {
          contact__form__hide: isSuccess,
          contact__form__hidden: isFormHidden,
        })}
        onAnimationEnd={onFormHideAnimationEnd}
        onSubmit={handleFormSubmit}
      >
        <Input
          isLegacy={false}
          title={t('name')}
          placeholder={t('name_placeholder')}
          value={values.name}
          error={errors.nameError}
          name="quote-name"
          onValueChange={setName}
        />
        <Input
          isLegacy={false}
          title={t('email')}
          placeholder={t('email_placeholder')}
          value={values.email}
          error={errors.emailError}
          name="quote-email"
          onValueChange={setEmail}
        />
        <Input
          isLegacy={false}
          title={t('project_description_title')}
          value={values.description}
          error={errors.descriptionError}
          name="quote-description"
          multiline
          onValueChange={setDescription}
          placeholder={t('project_description_placeholder')}
        />
        <Picker
          isLegacy={false}
          options={sourceOptions}
          value={values.source}
          onChange={onSourceChange}
        />
        <div>
          <Text
            color="light"
            type="p"
            className="contact__form__description p-updated"
          >
            {t('contact_form_description') + ' '}
            <a
              target="_blank"
              rel="noopener noreferrer"
              className="contact__form__link"
              href={`https://stormotion.io/privacy-policy/`}
            >
              {t('privacy_policy')}
            </a>
            {' ' + t('contact_form_post_description')}
          </Text>
          {SubmitButton}
        </div>
      </form>

      <div
        className={classNames('contact__success', {
          contact__success__show: isFormHidden,
        })}
      >
        <Text color="light" type="p" className="contact__success__title">
          {t('contact_form_success_title')}
        </Text>
        <Text
          dim={'40'}
          color="light"
          type="p"
          className="contact__success__description h6 p-updated"
        >
          {t('contact_form_success_description')}
        </Text>
        <Text
          dim={'40'}
          color="light"
          type="p"
          className="contact__success__description h6 p-updated"
        >
          {t('contact_form_success_description_2')}
        </Text>
        <Button
          size="regular"
          href={'/'}
          rounded
          color="dark-grey"
          className="contact__success__button"
        >
          {t('contact_form_success_button')}
        </Button>
      </div>
    </div>
  );
};

export default React.memo(ContactForm);
